import Hotjar from '@hotjar/browser';
import { createSelector } from '@reduxjs/toolkit';
import mixpanel from 'mixpanel-browser';

import { toursSyncPayload } from '@/components/Tours/persistence/sync';
import { isDev } from '@/lib/utils';
import { selectTourGate } from '@/redux/slices/tours/selectors';

import store from '../redux/store';

let isMPInitialized = false;
let isHotjarInitialized = false;
const isCookieEnabled = () => {
  try {
    // "navigator.cookieEnabled" is not reliable so we check it by creating/deleting a test cookie
    // create cookie
    document.cookie = 'cookietest=1';
    const ret = document.cookie.indexOf('cookietest=') !== -1;
    // delete cookie
    document.cookie = 'cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT';
    return ret;
  } catch (e) {
    return false;
  }
};

const initializeHotjar = () => {
  if (!isHotjarInitialized) {
    try {
      const hotjarVersion = 6;
      Hotjar.init(process.env.PREACT_APP_HOTJAR_SITE_ID, hotjarVersion, {
        debug: isDev(),
      });
      isHotjarInitialized = true;
    } catch (err) {
      console.error('error on Hotjar initializing', err);
    }
  }
};

export const initializeMP = () => {
  try {
    const storageMethod = isCookieEnabled() ? 'cookie' : 'localStorage';
    if (!isMPInitialized) {
      const proxyApiURL = process.env.PREACT_APP_API_AUTHORITY.includes('localhost')
        ? 'api.zoolife.live'
        : process.env.PREACT_APP_API_AUTHORITY;
      mixpanel.init(process.env.PREACT_APP_MIXPANEL_TOKEN, {
        debug: isDev(),
        api_host: `https://${proxyApiURL}/mp`,
        ignore_dnt: true,
        persistence: storageMethod,
      });
      isMPInitialized = true;
    }
    initializeHotjar();
  } catch (err) {
    console.error('error on mixpanel initializing ', err);
  }
};

const createLeanUserObject = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([key]) => !['gaClientIds', 'sessionDurations'].includes(key)));

const getUrlParams = () => {
  const excludeKeys = ['time', 'distinct_id', 'id', 'Id', 'ID', 'ml', 'pass', 'bundleUserPass'];
  const urlParams = new URLSearchParams(typeof window !== 'undefined' && window.location.search);
  return Object.fromEntries(Array.from(urlParams.entries()).filter(([key]) => !excludeKeys.includes(key)));
};

// because mixpanel has maximum level of nesting, we need to flatten the user object to avoid errors
const selectFormattedUser = createSelector(
  (state) => state.user,
  // The MERGED tour state, not `user.tours`. The user object carries only what the server holds,
  // which is a sync baseline and not a current value: it is seeded when the session lands and is
  // never written to again, so a tour finished in this session would be reported as unfinished on
  // every event until the next reload. `selectTourGate` overlays the client's progress on top,
  // which is also the only view that knows about a run finished on another device.
  (state) => selectTourGate(state).tours,
  (user, mergedTours) => {
    const { subscription, ...rest } = user;
    // `ability` is the packed map the checks run on, and the one thing in here Mixpanel must not
    // see. Everything else at the user root — `entitlements`, `userProducts`, `habitatsAccessible`,
    // `everPaid` — is already in the shape Mixpanel wants and rides along on the spread, which is
    // exactly why the API puts it there: a build that predates it forwards the same values.
    //
    // Deprecated: `featuresUntil`, `habitatsAccessUntil` and the rest of the legacy `subscription`
    // are forwarded only until the Mixpanel reports move onto the custom properties (zl-api plan,
    // 4.4–4.6); this app reads none of them.
    const { featuresUntil, habitatsAccessUntil, ability, ...restSubscription } = subscription ?? {};
    return {
      ...rest,
      // After the spread, so it replaces the server copy rather than sitting beside it. Sent
      // through the same function the PATCH uses, which drops the one field that must not travel:
      // the step history is the bulky part and it stays on the device.
      tours: toursSyncPayload(mergedTours),
      subscription: restSubscription,
      featuresUntil,
      habitatsAccessUntil,
    };
  },
);

export const trackMP = (event, /** @type any */ options = null) => {
  try {
    const currentEvent = event;
    if (!isMPInitialized) {
      initializeMP();
    }
    if (!isHotjarInitialized) {
      initializeHotjar();
    }

    const user = selectFormattedUser(store.getState());
    const paramsObject = getUrlParams();
    const leanerUserObj = createLeanUserObject(user);

    const data = { ...leanerUserObj, ...options, ...paramsObject };

    if (leanerUserObj?.userId) {
      data.distinct_id = user.userId;
    }
    mixpanel.track(currentEvent, data);
    if (
      currentEvent === 'freemium-onboarding' ||
      currentEvent === 'login' ||
      currentEvent === 'stripe-checkout-completed' ||
      currentEvent === 'redeem-giftcard'
    ) {
      // if the user signs up OR logs in with a new device, we will let MP know that the random
      // distinct_id which was created automatically should be replaced with the user's mongo Id,
      // so we can trace the old user on any new device .
      if (user.userId) {
        try {
          const isBundleUser = !!user.userProducts?.some((held) => held.source === 'bundling');
          if (new Date(user.createdAt).toDateString() === new Date().toDateString()) {
            // if user is created today
            Hotjar.event('first-day-user');
            if (isBundleUser) {
              Hotjar.event('first-day-bundle-user');
            }
          } else if (isBundleUser) {
            Hotjar.event('bundle-user');
          }
        } catch (err) {
          console.error('error on hotjar event tracking', err);
        }
        mixpanel.identify(user.userId);
        // Storing user profile on MP
        mixpanel.people.set(user);
        Hotjar.identify(user.userId, user);
      }
    }
  } catch (err) {
    console.error('error on mixpanel tracking', err);
  }
};

/**
 * Who Mixpanel currently thinks it is tracking: a `user._id` once identified, or its own
 * `$device:<uuid>` while anonymous.
 *
 * This is the shared record of the tracking identity — a cookie on `.zoolife.tv`, so the educators app
 * reads the same value — and `reset()` rotates it, which makes it the thing to consult before
 * resetting again. See `isTrackingIdentityStale`.
 *
 * `undefined` when Mixpanel has not initialized, which is the honest answer: no identity, nothing owed.
 */
export const getTrackingId = () => {
  try {
    return mixpanel.get_distinct_id();
  } catch (err) {
    // Reading an identity is never worth a crash; callers treat absent as "nothing owed".
    return undefined;
  }
};

/**
 * Forget who we were tracking: new anonymous `$device_id`, super properties and queued profile
 * updates dropped, session recording restarted.
 *
 * **Per instance, not once per logout.** The `distinct_id` cookie is shared across `.zoolife.tv`, but
 * each app loads it into memory only at init and every save rewrites the whole cookie from that copy.
 * An app that skips its own reset therefore keeps the departed user in memory and writes them back
 * over the cookie on its next event — which is why this exists here at all rather than leaning on the
 * educators app to reset the shared cookie for both. Its `resetTracking` is this function's twin.
 *
 * Hotjar goes with it. Unlike Mixpanel's, its identity is per-page rather than shared, so it is
 * always ours to clear; `identify(null)` is how it is un-identified.
 */
export const resetMP = () => {
  try {
    mixpanel.reset();
  } catch (err) {
    console.error('error on resetting mixpanel', err);
  }
  try {
    if (isHotjarInitialized) {
      Hotjar.identify(null, {});
    }
  } catch (err) {
    console.error('error on resetting hotjar identity', err);
  }
};
